import * as pdfjsLib from 'pdfjs-dist';
import PdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';

// PDF.js renders pages off the main thread. Let Vite bundle and instantiate the
// worker (?worker) rather than pointing workerSrc at a raw .mjs URL: the latter
// is served as application/octet-stream by static hosts (nginx) and rejected by
// strict module-script MIME checks. A single shared worker serves every document,
// whether rendered on screen (PdfViewer) or off screen (the categorize run).
//
// BUILT ON FIRST USE, not when this module loads: it is in the bundle the published tutorial
// loads too, which never opens a PDF, and a worker made at load sent every visitor's phone
// 370 KB of PDF reader (Louis 2026-10-23).
let port: Worker | null = null;

// AND ONE PDFWorker AROUND IT, HELD, because destroying a document must not destroy the
// worker underneath it.
//
// `getDocument` with no `worker` builds its own PDFWorker around the shared port, and that
// task then OWNS it: `task.destroy()` — which every caller here does, correctly, in a
// finally — tears down the port with the document. The next getDocument throws
// "PDFWorker.create - the worker is being destroyed", and so does every load already in
// flight.
//
// Which stayed hidden for as long as nothing opened the PDF twice at once. The parts table
// renders its crops from it, the step crops run opens it again to render the step bands,
// and pressing that button while the table was still cutting produced five exceptions and a
// run that ended before it started — the failure looked like a button that did nothing.
//
// Passing the worker explicitly is the documented way to share one: `task._worker` is only
// set when getDocument had to make its own, and destroy() only tears down what is in that
// field. Hand it a worker and destroy() closes the document and leaves the port alone.
//
// `PDFWorker.create`, NOT `new PDFWorker`. The constructor with no port goes off to build a
// worker from `GlobalWorkerOptions.workerSrc` — which is not set here and must not be, since
// the whole reason this app hands PDF.js a `?worker` PORT is that nginx serves the raw .mjs
// as octet-stream and the browser refuses it as a module script. `new PDFWorker({})` threw
// `No "GlobalWorkerOptions.workerSrc" specified.` on every load in the app. `create` is the
// one that takes the port and returns the instance already cached against it.
let shared: import('pdfjs-dist').PDFWorker | null = null;
const sharedWorker = () => {
  // A destroyed worker has TERMINATED its port, and a terminated port cannot be revived —
  // so a new one is built and given to the library too, or the next plain getDocument would
  // hand PDF.js a dead thread. Nothing here destroys it any more; this is for the browser
  // killing the worker under a backgrounded tab.
  if (!port || shared?.destroyed) {
    port = new PdfjsWorker();
    pdfjsLib.GlobalWorkerOptions.workerPort = port;
    shared = null;
  }
  return (shared ??= pdfjsLib.PDFWorker.create({ port }));
};

// Open a PDF from raw bytes. Always go through here (never call getDocument
// directly) so every document shares the same options.
//
// `disableFontFace: true` makes PDF.js rasterize embedded fonts with its own
// glyph renderer instead of installing them as browser @font-face fonts. We
// render only to canvas (no selectable text layer), and the single shared worker
// serves many documents opened and closed repeatedly; the @font-face names then
// collide across reopens and glyphs come out scrambled. Rasterizing sidesteps
// that entirely (and keeps the off-screen page images we send to the models clean).
export function loadPdf(data: ArrayBuffer | Uint8Array): PDFDocumentLoadingTask {
  return pdfjsLib.getDocument({ data, disableFontFace: true, worker: sharedWorker() });
}

// ONE PAGE, RENDERED, and what every export below starts from.
//
// The four differ in what they do to a rendered page — nothing, draw boxes on it, crop it,
// mask it — and each carried its own copy of getting there: the page, the viewport scaled
// to a target width, the canvas, the context, the two Math.floors. Four copies of the
// render options is four places for a document to be opened one way here and another way
// there, which is exactly the class of bug disableFontFace above exists to fix.
//
// Null when the browser gives no 2D context. Every caller answers null the same way,
// because a page that did not render is not an error worth stopping a run for — it is one
// page's image missing, and the run says so by its count.
async function renderPage(doc: PDFDocumentProxy, n: number, targetWidth: number): Promise<{
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
} | null> {
  const pdfPage = await doc.getPage(n);
  const base = pdfPage.getViewport({ scale: 1 });
  const viewport = pdfPage.getViewport({ scale: targetWidth / base.width });
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const w = Math.floor(viewport.width);
  const h = Math.floor(viewport.height);
  canvas.width = w;
  canvas.height = h;
  await pdfPage.render({ canvas, canvasContext: ctx, viewport }).promise;
  return { canvas, ctx, w, h };
}

// Render any page off-screen to a PNG data URL, scaled to a target pixel width
// (used to categorize pages without touching the on-screen canvas).
export async function renderPdfPageToImage(
  doc: PDFDocumentProxy,
  n: number,
  targetWidth = 1000,
): Promise<string | null> {
  try {
    const page = await renderPage(doc, n, targetWidth);
    return page ? page.canvas.toDataURL('image/png') : null;
  } catch {
    return null;
  }
}

// One part's box to draw on the annotated page: its normalized [x0,y0,x1,y1], a
// short tag to label it, and whether it's one the model is being asked to fix.
export type AnnotBox = { bbox: [number, number, number, number]; tag: string; fix?: boolean };

// Render a page off-screen with EVERY part's current box drawn on it, each tagged,
// so the model can see all placements at once and correct the misplaced ones
// (Phase 2's annotated second pass). `fix` boxes (the ones to re-judge) are drawn in
// red to draw the model's eye; the rest are green context. Returns a PNG data URL.
export async function renderPdfPageAnnotated(
  doc: PDFDocumentProxy,
  n: number,
  boxes: AnnotBox[],
  targetWidth = 1600,
): Promise<string | null> {
  try {
    const page = await renderPage(doc, n, targetWidth);
    if (!page) return null;
    const { canvas, ctx, w: W, h: H } = page;

    const font = Math.max(12, Math.round(W / 70));
    ctx.font = `bold ${font}px sans-serif`;
    ctx.textBaseline = 'top';
    for (const b of boxes) {
      const [x0, y0, x1, y1] = b.bbox;
      const px = x0 * W;
      const py = y0 * H;
      const color = b.fix ? '#e11d48' : '#10b981';
      ctx.lineWidth = b.fix ? 4 : 2;
      ctx.strokeStyle = color;
      ctx.strokeRect(px, py, (x1 - x0) * W, (y1 - y0) * H);
      // Tag chip just above the box (tucked inside the top when there's no room above),
      // so a label never buries a neighbouring drawing.
      const pad = Math.round(font * 0.3);
      const chipH = font + pad * 2;
      const chipW = ctx.measureText(b.tag).width + pad * 2;
      const ty = py - chipH >= 0 ? py - chipH : py;
      ctx.fillStyle = color;
      ctx.fillRect(px, ty, chipW, chipH);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(b.tag, px + pad, ty + pad);
    }
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

// Render a page and return only the vertical band [top, bottom] (0..1 from the
// top) as a PNG data URL. Used to send just the in-category section of a page to
// the model. A full-page band (0..1) returns the whole page.
export async function renderPdfPageBand(
  doc: PDFDocumentProxy,
  n: number,
  top: number,
  bottom: number,
  targetWidth = 1100,
): Promise<string | null> {
  try {
    const page = await renderPage(doc, n, targetWidth);
    if (!page) return null;
    const { canvas: full, w, h } = page;
    if (top <= 0 && bottom >= 1) return full.toDataURL('image/png');

    const y0 = Math.max(0, Math.floor(top * h));
    const y1 = Math.min(h, Math.ceil(bottom * h));
    const bandH = Math.max(1, y1 - y0);
    const band = document.createElement('canvas');
    const bctx = band.getContext('2d');
    if (!bctx) return null;
    band.width = w;
    band.height = bandH;
    bctx.drawImage(full, 0, y0, w, bandH, 0, 0, w, bandH);
    return band.toDataURL('image/png');
  } catch {
    return null;
  }
}

// Render the FULL page but paint everything OUTSIDE the vertical band [top, bottom] white,
// so only the in-category section shows while the image keeps the page's real dimensions.
// Unlike renderPdfPageBand (which CROPS to a strip), this is for asking the model for
// bounding boxes: the model measures box_2d against the whole page, so the box comes back
// page-relative with no band transform to apply — and, crucially, a full-page aspect ratio
// keeps those boxes accurate, where a cropped strip skews them all by one offset. A
// full-page band (0..1) returns the whole page untouched.
export async function renderPdfPageMasked(
  doc: PDFDocumentProxy,
  n: number,
  top: number,
  bottom: number,
  targetWidth = 1600,
): Promise<string | null> {
  try {
    const page = await renderPage(doc, n, targetWidth);
    if (!page) return null;
    const { canvas, ctx, w: W, h: H } = page;
    if (top > 0 || bottom < 1) {
      ctx.fillStyle = '#ffffff';
      if (top > 0) ctx.fillRect(0, 0, W, Math.floor(top * H));
      if (bottom < 1) {
        const yb = Math.ceil(bottom * H);
        ctx.fillRect(0, yb, W, H - yb);
      }
    }
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
